import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getDepartmentsAction,
  deleteDepartmentAction,
} from "../../../store/actions/DepartmentAction";
import swal from "sweetalert";
import Loader from "../Common/Loader";

const Department = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const departmentState = useSelector((state) => state.department || {});
  const { departments = [], loading = false, error = null, num_rows = 0 } = departmentState;

  const [searchQueries, setSearchQueries] = useState({
    department_id: "",
    department_name: "",
    department_description: "",
    department_status: "",
  });

  const [sortField, setSortField] = useState("department_id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  useEffect(() => {
    const fetchDepartments = async () => {
      setIsLoading(true);
      try {
        if (location.state) {
          const { currentPage, searchQueries, sortField, sortOrder } = location.state;
          if (currentPage !== undefined) setCurrentPage(currentPage);
          if (searchQueries) setSearchQueries(searchQueries);
          if (sortField) setSortField(sortField);
          if (sortOrder) setSortOrder(sortOrder);
        }
        await fetchDepartmentsFromServer();
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDepartments();
  }, [dispatch]);

  const fetchDepartmentsFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = 0
  ) => {
    try {
      await dispatch(getDepartmentsAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order));
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchDepartmentsFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      page
    );
  };

  // Handle search - Modified to clear other fields
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries and set only the current field
    const clearedQueries = {
      department_id: "",
      department_name: "",
      department_description: "",
      department_status: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    // Track active search
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);

    fetchDepartmentsFromServer(field, keyword, sortField, sortOrder, true, 0);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    fetchDepartmentsFromServer(
      activeSearchField,
      activeSearchKeyword,
      field,
      order,
      true,
      currentPage
    );
  };

  const handleEdit = (department) => {
    navigate(`/department/update-department/${department.department_id}`, {
      state: {
        department,
        returnTo: {
          currentPage,
          searchQueries,
          sortField,
          sortOrder,
        },
      },
    });
  };

  const handleDelete = (dept) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, this department cannot be recovered!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setIsLoading(true);
          await dispatch(deleteDepartmentAction(dept));
          // Refresh current page after deletion
          fetchDepartmentsFromServer(
            activeSearchField,
            activeSearchKeyword,
            sortField,
            sortOrder,
            true,
            currentPage
          );
        } catch (err) {
          console.error("Delete failed:", err);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  // Server already returns paginated data
  const paginatedData = departments;
  const totalPages = Math.ceil(num_rows / itemsPerPage);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="row mb-3">
        <div className="col-xl-12 text-end">
          <Link
            to="/add-department"
            state={{
              returnTo: {
                currentPage,
                searchQueries,
                sortField,
                sortOrder,
              },
            }}
            className="btn btn-primary btn-rounded"
          >
            + Add Department
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  {/* Responsive Table */}
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("department_id")} style={{ cursor: "pointer" }}>
                            ID{" "}
                            {sortField === "department_id" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.department_id}
                              onChange={(e) => handleSearch(e, "department_id")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search ID"
                            />
                          </th>
                          <th onClick={() => handleSort("department_name")} style={{ cursor: "pointer" }}>
                            Name{" "}
                            {sortField === "department_name" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.department_name}
                              onChange={(e) => handleSearch(e, "department_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Name"
                            />
                          </th>
                          <th onClick={() => handleSort("department_description")} style={{ cursor: "pointer" }}>
                            Description{" "}
                            {sortField === "department_description" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.department_description}
                              onChange={(e) =>
                                handleSearch(e, "department_description")
                              }
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Description"
                            />
                          </th>
                          <th onClick={() => handleSort("department_status")} style={{ cursor: "pointer" }}>
                            Status{" "}
                            {sortField === "department_status" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.department_status}
                              onChange={(e) => handleSearch(e, "department_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((dept) => (
                            <tr key={dept.department_id}>
                              <td>{dept.department_id}</td>
                              <td>{dept.department_name}</td>
                              <td>{dept.department_description}</td>
                              <td>
                                <span
                                  className={`badge px-3 py-1 rounded-pill text-white ${
                                    dept.department_status.toLowerCase() === "active"
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {dept.department_status}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex justify-content-around">
                                  <button
                                    className="edit me-2 btn btn-link p-0"
                                    onClick={() => handleEdit(dept)}
                                    title="Edit Department"
                                  >
                                    <i className="fa fa-pencil-alt text-success fs-18" />
                                  </button>
                                  <span
                                    className="delete me-2"
                                    onClick={() => handleDelete(dept)}
                                    style={{ cursor: "pointer" }}
                                    title="Delete Department"
                                  >
                                    <i className="fa fa-trash text-danger fs-18" />
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4">
                              {loading ? "Loading..." : "No departments found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {num_rows > 0 && (
                    <div
                      className="pagination-wrapper"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        marginTop: "20px",
                        gap: "10px",
                      }}
                    >
                      <div>
                        Showing {departments?.length === 0 ? 0 : currentPage * itemsPerPage + 1} to{" "}
                        {Math.min((currentPage + 1) * itemsPerPage, num_rows)} of {num_rows} entries
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "center" }}>
                        {/* Previous button */}
                        <button
                          // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                          disabled={currentPage === 0}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === 0 ? "not-allowed" : "pointer",
                            opacity: currentPage === 0 ? 0.5 : 1,
                          }}
                        >
                          &lt;
                        </button>

                        {/* Dynamic page buttons */}
                        {(() => {
                          const pages = [];
                          const maxPagesToShow = 5;
                          const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                          const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                          if (startPage > 0) {
                            pages.push(
                              <button
                                key={0}
                                onClick={() => handlePageChange(0)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                1
                              </button>
                            );
                            if (startPage > 1) {
                              pages.push(
                                <span key="dots1" style={{ padding: "6px 12px" }}>
                                  ...
                                </span>
                              );
                            }
                          }

                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i;
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: isActive ? "#007bff" : "#fff",
                                  color: isActive ? "#fff" : "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {i + 1}
                              </button>
                            );
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span key="dots2" style={{ padding: "6px 12px" }}>
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages - 1}
                                onClick={() => handlePageChange(totalPages - 1)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}

                        {/* Next button */}
                        <button
                          // onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                          disabled={currentPage === totalPages - 1}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                          }}
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Department;
